// 📄 pages/Proyectos/components/ProyectoForm.tsx
// Formulario compartido entre CrearProyecto y EditarProyecto: plantilla
// (solo al crear), módulos, cliente (empresa) y nombre del proyecto; si el
// proyecto usa SAP Ariba, además soluciones e integración ERP (ver
// plan-proyectos-generales.md). Campos específicos de cada modo (p. ej.
// "tu rol" solo al crear) se inyectan vía `children`.
import React, { useEffect, useState } from 'react';
import { RefreshCw, AlertTriangle, Pencil, Plus, X } from 'lucide-react';
import { listClientesForOrg, createCliente, Cliente } from '../../../services/clienteService.ts';
import CheckboxCardGroup from '../../../components/ui/CheckboxCardGroup.tsx';
import { SOLUCION_OPTIONS } from '../solucionesConfig.ts';
import { TIPOS_ERP, labelTipoErp } from '../erpConfig.ts';
import { MODULOS, PLANTILLAS, ModuloId, PlantillaId } from '../modulosConfig.ts';

export interface ProyectoFormValues {
  clienteId: string;
  clienteNombre: string;
  nombre: string;
  solucionesActivas: string[];
  modulos: string[];
  plantilla: PlantillaId | '';
  // Áreas para clasificar incidentes, horas, pruebas y documentación (sin SAP).
  areas: string[];
  erpTipo: string;
  erpVersion: string;
  // Mesa de ayuda (plan-tickets-proyecto.md). Solo se edita en EditarProyecto.
  recibeTickets: boolean;
}

interface ProyectoFormProps {
  // Sub-fase G2: de qué organización se listan los clientes seleccionables.
  // CrearProyecto pasa la org activa (donde va a nacer el proyecto);
  // EditarProyecto pasa la org DUEÑA del proyecto que se está editando
  // (project.orgId) — nunca la org activa del switcher, que puede ser
  // otra en el momento de editar.
  orgId: string;
  initialClienteId?: string;
  initialNombre?: string;
  initialSolucionesActivas?: string[];
  initialModulos?: string[];
  initialAreas?: string[];
  // Solo al crear: muestra las plantillas, que preseleccionan módulos.
  mostrarPlantillas?: boolean;
  initialErpTipo?: string;
  initialErpVersion?: string;
  // Solo EditarProyecto lo pasa: muestra el interruptor de la mesa de ayuda.
  initialRecibeTickets?: boolean;
  submitLabel: string;
  savingLabel: string;
  onSubmit: (values: ProyectoFormValues) => Promise<void>;
  onCancel: () => void;
  children?: React.ReactNode;
  // Sub-fase K9: cambiar erpTipo (Cloud↔On-Premise) es una acción "pesada",
  // separada del resto de "Editar Proyecto" — solo EditarProyecto.tsx pasa
  // `modoEdicionProyecto`. CrearProyecto.tsx no lo pasa: al crear, todavía
  // no hay ningún tag "actual" que romper, el selector normal alcanza.
  // `onCambiarErpTipo` además controla si el botón "Cambiar" aparece — si
  // falta (el usuario no es el creador ni admin), erpTipo se muestra de
  // solo lectura, sin ninguna vía para tocarlo.
  modoEdicionProyecto?: boolean;
  onCambiarErpTipo?: (nuevoErpTipo: string) => Promise<void>;
  nombreProyectoParaConfirmar?: string;
}

export default function ProyectoForm({
  orgId,
  initialClienteId,
  initialNombre,
  initialSolucionesActivas,
  initialModulos,
  initialAreas,
  mostrarPlantillas,
  initialErpTipo,
  initialErpVersion,
  initialRecibeTickets,
  submitLabel,
  savingLabel,
  onSubmit,
  onCancel,
  children,
  modoEdicionProyecto,
  onCambiarErpTipo,
  nombreProyectoParaConfirmar
}: ProyectoFormProps) {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [clienteId, setClienteId] = useState(initialClienteId || '');
  const [nombre, setNombre] = useState(initialNombre || '');
  const [solucionesActivas, setSolucionesActivas] = useState<string[]>(initialSolucionesActivas || ['commerce']);
  // Sin módulos iniciales (crear desde cero): la plantilla "Proyecto general".
  const [plantilla, setPlantilla] = useState<PlantillaId | ''>(mostrarPlantillas && !initialModulos ? 'general' : '');
  const [modulos, setModulos] = useState<string[]>(initialModulos || ['gestion']);
  const usaSap = modulos.includes('sap-ariba');
  const [areas, setAreas] = useState<string[]>(initialAreas || []);
  const [nuevaArea, setNuevaArea] = useState('');
  // Cliente obligatorio salvo que se marque "Proyecto interno" (decisión del
  // usuario, G2). Al editar, un proyecto sin cliente ya es interno.
  const [interno, setInterno] = useState(!!modoEdicionProyecto && !initialClienteId);
  const [nuevoClienteAbierto, setNuevoClienteAbierto] = useState(false);
  // SAP Ariba siempre necesita cliente: ahí "interno" no aplica.
  const sinCliente = interno && !usaSap;
  const sapAlInicio = (initialModulos || []).includes('sap-ariba');
  const [erpTipo, setErpTipo] = useState(initialErpTipo || '');
  const [erpVersion, setErpVersion] = useState(initialErpVersion || '');
  const [recibeTickets, setRecibeTickets] = useState(!!initialRecibeTickets);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modalErpTipoAbierto, setModalErpTipoAbierto] = useState(false);
  const [erpTipoElegido, setErpTipoElegido] = useState(erpTipo);
  const [confirmNombre, setConfirmNombre] = useState('');
  const [cambiandoErpTipo, setCambiandoErpTipo] = useState(false);
  const [errorErpTipo, setErrorErpTipo] = useState<string | null>(null);

  const abrirModalErpTipo = () => {
    setErpTipoElegido(erpTipo);
    setConfirmNombre('');
    setErrorErpTipo(null);
    setModalErpTipoAbierto(true);
  };

  const handleConfirmarCambioErpTipo = async () => {
    if (!onCambiarErpTipo) return;
    setErrorErpTipo(null);
    setCambiandoErpTipo(true);
    try {
      await onCambiarErpTipo(erpTipoElegido);
      setErpTipo(erpTipoElegido);
      setModalErpTipoAbierto(false);
    } catch (err: any) {
      setErrorErpTipo(err.message || 'Error al cambiar el tipo de ERP.');
    } finally {
      setCambiandoErpTipo(false);
    }
  };

  useEffect(() => {
    if (!orgId) { setClientes([]); setLoadingClientes(false); return; }
    listClientesForOrg(orgId)
      .then((list) => {
        // Solo clientes activos son seleccionables para un proyecto nuevo,
        // salvo que ya sea el cliente asignado al proyecto que se está
        // editando — si no, editar un proyecto cuyo cliente pasó a
        // inactivo lo dejaría sin poder guardar ningún otro cambio.
        const seleccionables = list.filter(c => c.activo || c.id === initialClienteId);
        setClientes(seleccionables);
      })
      .catch((err) => console.error('Error cargando clientes:', err))
      .finally(() => setLoadingClientes(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const agregarArea = () => {
    const a = nuevaArea.trim();
    if (a && !areas.some((x) => x.toLowerCase() === a.toLowerCase()) && areas.length < 20) setAreas((prev) => [...prev, a]);
    setNuevaArea('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const cliente = clientes.find(c => c.id === clienteId);
    if (clienteId && !sinCliente && !cliente) { setError('Selecciona un cliente válido.'); return; }
    if (usaSap && !cliente) { setError('Un proyecto SAP Ariba necesita un cliente.'); return; }
    if (!sinCliente && !cliente) { setError('Selecciona el cliente, o marca "Proyecto interno" si no tiene.'); return; }
    if (!nombre.trim()) { setError('El nombre del proyecto es obligatorio.'); return; }
    if (usaSap && solucionesActivas.length === 0) { setError('Selecciona al menos una solución de SAP Ariba.'); return; }

    setSaving(true);
    try {
      await onSubmit({
        clienteId: sinCliente ? '' : cliente?.id || '',
        clienteNombre: sinCliente ? '' : cliente?.name || '',
        nombre: nombre.trim(),
        // Sin SAP Ariba se conservan las soluciones que ya tuviera (los datos no se borran).
        solucionesActivas: usaSap ? solucionesActivas : (initialSolucionesActivas || []),
        modulos,
        plantilla,
        areas,
        erpTipo,
        erpVersion: erpVersion.trim(),
        recibeTickets: recibeTickets && !sinCliente
      });
    } catch (err: any) {
      setError(err.message || 'Ocurrió un error.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
      {mostrarPlantillas && (
        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
            Tipo de proyecto
          </label>
          <div className="grid grid-cols-1 gap-2">
            {PLANTILLAS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { setPlantilla(p.id); setModulos(p.modulos); setAreas(p.areas); }}
                className={`text-left p-3 rounded-lg border transition-all ${plantilla === p.id ? 'bg-blue-500/10 border-blue-500/50' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
              >
                <span className="block text-sm font-semibold text-white">{p.icon} {p.label}</span>
                <span className="block text-[11px] text-gray-500 mt-1">{p.descripcion}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
          Módulos
        </label>
        <div className="space-y-2">
          {Object.values(MODULOS).map((m) => {
            const activo = modulos.includes(m.id);
            return (
              <label key={m.id} className={`flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/10 transition-all ${m.obligatorio ? 'opacity-80' : 'hover:bg-white/10 cursor-pointer'}`}>
                <input
                  type="checkbox"
                  className="accent-blue-500 w-4 h-4 mt-0.5"
                  checked={activo}
                  disabled={m.obligatorio}
                  onChange={() => setModulos((prev) => (activo ? prev.filter((x) => x !== m.id) : [...prev, m.id as ModuloId]))}
                />
                <span>
                  <span className="block text-sm text-white">{m.icon} {m.label}{m.obligatorio ? ' (siempre incluido)' : ''}</span>
                  <span className="block text-[11px] text-gray-500 mt-0.5">{m.descripcion}</span>
                </span>
              </label>
            );
          })}
        </div>
        {sapAlInicio && !usaSap && (
          <p className="text-[11px] text-amber-300/90 mt-2 flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            Se ocultarán las secciones de SAP Ariba de este proyecto. Sus datos no se borran: vuelven si lo activas de nuevo.
          </p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Cliente (empresa)
          </label>
          {!sinCliente && (
            <button type="button" onClick={() => setNuevoClienteAbierto(true)} className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300">
              <Plus className="w-3.5 h-3.5" /> Nuevo cliente
            </button>
          )}
        </div>
        {!sinCliente && (loadingClientes ? (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <RefreshCw className="w-4 h-4 animate-spin" /> Cargando clientes...
          </div>
        ) : clientes.length === 0 ? (
          <p className="text-sm text-gray-500">Todavía no hay clientes: créalo con "Nuevo cliente".</p>
        ) : (
          <select
            value={clienteId}
            onChange={(e) => setClienteId(e.target.value)}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Selecciona un cliente</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        ))}
        {!usaSap && (
          <label className="flex items-center gap-2 text-xs text-gray-400 mt-2 cursor-pointer">
            <input type="checkbox" checked={interno} onChange={(e) => setInterno(e.target.checked)} className="accent-blue-500 w-3.5 h-3.5" />
            Proyecto interno (sin cliente): un producto propio, un estudio o una herramienta interna.
          </label>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
          Nombre del Proyecto
        </label>
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder={usaSap ? "Ej. Implementación Ariba - Fase 1" : "Ej. App de pedidos para restaurante"}
          className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
          required
        />
      </div>

      {modoEdicionProyecto && !sinCliente && (
        <label className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-3 cursor-pointer">
          <input
            type="checkbox"
            checked={recibeTickets}
            onChange={(e) => setRecibeTickets(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            <span className="block text-sm font-medium text-gray-200">Recibe tickets de soporte (mesa de ayuda)</span>
            <span className="block text-[11px] text-gray-500">
              Los usuarios del cliente pueden abrir tickets del día a día (no veo una OC, mi factura fue rechazada) en este proyecto y desde la extensión de Chrome. Los recibe el rol Soporte del proyecto (o el PM si no hay). Al apagarla, los tickets que ya existen se siguen viendo y atendiendo.
            </span>
          </span>
        </label>
      )}

      {!usaSap && (
        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
            Áreas del proyecto (opcional)
          </label>
          <p className="text-[11px] text-gray-500 mb-2">Para clasificar incidentes, horas, pruebas y documentación. Ej.: Frontend, Backend, Cocina, Caja.</p>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {areas.map((a) => (
              <span key={a} className="flex items-center gap-1 text-xs bg-white/10 text-gray-200 rounded-full pl-2.5 pr-1 py-0.5">
                {a}
                <button type="button" onClick={() => setAreas((prev) => prev.filter((x) => x !== a))} className="p-0.5 rounded-full hover:bg-white/10" title="Quitar">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              value={nuevaArea}
              onChange={(e) => setNuevaArea(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarArea(); } }}
              placeholder="Nueva área"
              maxLength={40}
              className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-1.5 text-sm"
            />
            <button type="button" onClick={agregarArea} className="px-3 py-1.5 rounded-lg text-xs bg-white/5 hover:bg-white/10 border border-white/10">Agregar</button>
          </div>
        </div>
      )}

      {usaSap && <>
      <div>
        <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
          Soluciones de SAP Ariba
        </label>
        <CheckboxCardGroup
          options={SOLUCION_OPTIONS}
          selected={solucionesActivas}
          onChange={setSolucionesActivas}
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
          Integración ERP
        </label>
        <p className="text-xs text-gray-500 mb-2">Sistema ERP con el que se integra Ariba en este proyecto.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Tipo de ERP</label>
            {modoEdicionProyecto ? (
              <div className="flex items-center gap-2">
                <span className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-300">
                  {labelTipoErp(erpTipo) || 'Sin especificar'}
                </span>
                {onCambiarErpTipo && (
                  <button
                    type="button"
                    onClick={abrirModalErpTipo}
                    title="Cambiar tipo de ERP (Cloud ↔ On-Premise) — acción sensible, requiere confirmación"
                    className="flex-shrink-0 flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Cambiar
                  </button>
                )}
              </div>
            ) : (
              <select
                value={erpTipo}
                onChange={(e) => setErpTipo(e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Sin especificar</option>
                {TIPOS_ERP.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            )}
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Versión de ERP</label>
            <input
              value={erpVersion}
              onChange={(e) => setErpVersion(e.target.value)}
              placeholder="Ej. ECC 6.0 EHP8"
              className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>
      </>}

      {children}

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 py-2 rounded-lg text-sm font-semibold transition-all"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed py-2 rounded-lg text-sm font-semibold transition-all"
        >
          {saving ? savingLabel : submitLabel}
        </button>
      </div>
    </form>

    {nuevoClienteAbierto && (
      <NuevoClienteModal
        orgId={orgId}
        onClose={() => setNuevoClienteAbierto(false)}
        onCreado={(c) => { setClientes((prev) => [...prev, c].sort((x, y) => x.name.localeCompare(y.name))); setClienteId(c.id); setNuevoClienteAbierto(false); }}
      />
    )}

    {modalErpTipoAbierto && (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
        <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-medium text-white">Cambiar tipo de ERP</h3>
              <p className="text-sm text-gray-400 mt-1">
                Cloud y On-Premise usan modelos de configuración totalmente distintos. Al cambiar el tipo, la configuración actual (
                {labelTipoErp(erpTipo) || 'sin especificar'}) queda marcada como incompatible/histórica — no se borra, pero deja de ser la que se usa. Esta acción queda registrada.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Nuevo tipo de ERP</label>
            <select
              value={erpTipoElegido}
              onChange={(e) => setErpTipoElegido(e.target.value)}
              className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            >
              <option value="">Sin especificar</option>
              {TIPOS_ERP.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">
              Escribí <span className="text-white font-mono">{nombreProyectoParaConfirmar || nombre}</span> para confirmar
            </label>
            <input
              autoFocus
              value={confirmNombre}
              onChange={(e) => setConfirmNombre(e.target.value)}
              className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-red-500"
            />
          </div>

          {errorErpTipo && <p className="text-red-400 text-xs">{errorErpTipo}</p>}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setModalErpTipoAbierto(false)}
              className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmarCambioErpTipo}
              disabled={
                cambiandoErpTipo ||
                erpTipoElegido === erpTipo ||
                confirmNombre.trim() !== (nombreProyectoParaConfirmar || nombre).trim()
              }
              className="flex items-center gap-2 bg-red-600 hover:bg-red-500 disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
            >
              {cambiandoErpTipo ? <RefreshCw className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
              Cambiar tipo de ERP
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

/** Alta rápida de cliente sin salir del formulario (nombre y correo de contacto). */
function NuevoClienteModal({ orgId, onClose, onCreado }: { orgId: string; onClose: () => void; onCreado: (c: Cliente) => void }) {
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      const id = await createCliente({ name: nombre.trim(), email: email.trim(), apps: [], orgId });
      onCreado({ id, name: id, email: email.trim(), apps: [], activo: true, orgId });
    } catch (err: any) {
      setError(err.message || 'No se pudo crear el cliente.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-sm shadow-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium text-white">Nuevo cliente</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {error && <p className="text-red-400 text-xs">{error}</p>}
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Nombre de la empresa o persona</label>
          <input autoFocus value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej. Restaurante La Finca" className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Correo de contacto (opcional)</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contacto@empresa.com" className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5">Cancelar</button>
          <button type="button" onClick={guardar} disabled={guardando || !nombre.trim()} className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-white">
            {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crear
          </button>
        </div>
      </div>
    </div>
  );
}
